/**
 * Compliance Microservice API Client
 *
 * Uses the unified API client. Nginx routes /compliance/api/v1/* to the
 * saf3ai-compliance-microservice.
 */

import api from './apiClient';

const PREFIX = '/compliance/api/v1';

/**
 * Compliance API endpoints
 */
export const complianceAPI = {
  // Health check
  async getHealth() {
    const response = await api.get(`${PREFIX}/health`);
    return response.data;
  },

  // ==========================================================================
  // Built-in Frameworks
  // ==========================================================================

  /**
   * Get all built-in frameworks with their controls
   * @param {Object} params - Query parameters
   * @param {string} params.product_scope - Filter by product scope ('enterprise_ai', 'platform')
   */
  async getFrameworks(params = {}) {
    const response = await api.get(`${PREFIX}/frameworks`, { params });
    return response.data;
  },

  /**
   * Get all frameworks (built-in + custom)
   * @param {Object} params - Query parameters
   * @param {boolean} params.include_custom - Include custom frameworks (default: true)
   * @param {string} params.product_scope - Filter by product scope
   */
  async getAllFrameworks(params = {}) {
    const normalized = { ...params };
    // Backend requires module when include_custom=true.
    // If caller doesn't provide module, safely fetch built-in only.
    const wantsCustom = normalized.include_custom !== false;
    if (wantsCustom && !normalized.module) {
      normalized.include_custom = false;
    }
    const response = await api.get(`${PREFIX}/frameworks/all`, { params: normalized });
    // Backend returns {frameworks: [...], builtin_count: N, custom_count: M}
    // Return just the frameworks array for backwards compatibility
    return response.data.frameworks || response.data;
  },

  /**
   * Get a specific built-in framework by ID
   */
  async getFrameworkById(frameworkId) {
    const response = await api.get(`${PREFIX}/frameworks/${frameworkId}`);
    return response.data;
  },

  /**
   * Create a new built-in framework
   */
  async createFramework(frameworkData) {
    const response = await api.post(`${PREFIX}/frameworks`, frameworkData);
    return response.data;
  },

  /**
   * Update a built-in framework
   */
  async updateFramework(frameworkId, updates) {
    const response = await api.put(`${PREFIX}/frameworks/${frameworkId}`, updates);
    return response.data;
  },

  /**
   * Delete a built-in framework
   */
  async deleteFramework(frameworkId) {
    await api.delete(`${PREFIX}/frameworks/${frameworkId}`);
  },

  // ==========================================================================
  // Custom Frameworks
  // ==========================================================================

  /**
   * Get all custom frameworks for the authenticated organization
   * @param {Object} params - Query parameters
   * @param {string} params.product_scope - Filter by product scope
   * @param {string} [params.module] - Module name (e.g. 'gemini-enterprise')
   */
  async getCustomFrameworks(params = {}) {
    const response = await api.get(`${PREFIX}/frameworks/custom/`, { params });
    return response.data;
  },

  /**
   * Get a specific custom framework by ID
   * @param {string} frameworkId
   * @param {Object} params - Query parameters (e.g. { module: 'gemini-enterprise' })
   */
  async getCustomFrameworkById(frameworkId, params = {}) {
    const response = await api.get(`${PREFIX}/frameworks/custom/${frameworkId}`, { params });
    return response.data;
  },

  /**
   * Create a new custom framework
   * @param {Object} frameworkData - Framework data
   * @param {Object} params - Query parameters (e.g. { module: 'gemini-enterprise' })
   */
  async createCustomFramework(frameworkData, params = {}) {
    const response = await api.post(`${PREFIX}/frameworks/custom/`, frameworkData, { params });
    return response.data;
  },

  /**
   * Update a custom framework
   * @param {string} frameworkId
   * @param {Object} updates
   * @param {Object} params - Query parameters (e.g. { module: 'gemini-enterprise' })
   */
  async updateCustomFramework(frameworkId, updates, params = {}) {
    const response = await api.put(`${PREFIX}/frameworks/custom/${frameworkId}`, updates, { params });
    return response.data;
  },

  /**
   * Delete a custom framework
   * @param {string} frameworkId
   * @param {Object} params - Query parameters (e.g. { module: 'gemini-enterprise' })
   */
  async deleteCustomFramework(frameworkId, params = {}) {
    await api.delete(`${PREFIX}/frameworks/custom/${frameworkId}`, { params });
  },

  // ==========================================================================
  // Custom Framework Controls
  // ==========================================================================

  /**
   * Add a control to a custom framework
   * @param {string} frameworkId
   * @param {Object} controlData
   * @param {Object} params - Query parameters (e.g. { module: 'gemini-enterprise' })
   */
  async addControlToCustomFramework(frameworkId, controlData, params = {}) {
    const response = await api.post(
      `${PREFIX}/frameworks/custom/${frameworkId}/controls`,
      controlData,
      { params }
    );
    return response.data;
  },

  /**
   * Update a control within a custom framework
   * @param {string} frameworkId
   * @param {string} controlId
   * @param {Object} updates
   * @param {Object} params - Query parameters (e.g. { module: 'gemini-enterprise' })
   */
  async updateControlInCustomFramework(frameworkId, controlId, updates, params = {}) {
    const response = await api.put(
      `${PREFIX}/frameworks/custom/${frameworkId}/controls/${controlId}`,
      updates,
      { params }
    );
    return response.data;
  },

  /**
   * Delete a control from a custom framework
   * @param {string} frameworkId
   * @param {string} controlId
   * @param {Object} params - Query parameters (e.g. { module: 'gemini-enterprise' })
   */
  async deleteControlFromCustomFramework(frameworkId, controlId, params = {}) {
    await api.delete(
      `${PREFIX}/frameworks/custom/${frameworkId}/controls/${controlId}`,
      { params }
    );
  },

  // ==========================================================================
  // Custom Controls (Standalone CRUD)
  // ==========================================================================

  /**
   * Get all custom controls for the authenticated organization
   * @param {Object} params - Query parameters
   * @param {string} params.module - Module name (e.g. 'gemini-enterprise', 'microsoft-copilot', 'copilot-studio') - REQUIRED
   * @param {string} params.framework_id - Filter by custom framework ID (optional)
   */
  async getCustomControls(params = {}) {
    const response = await api.get(`${PREFIX}/custom-controls`, { params });
    return response.data;
  },

  /**
   * Get a specific custom control by ID
   * @param {string} controlId
   */
  async getCustomControlById(controlId) {
    const response = await api.get(`${PREFIX}/custom-controls/${controlId}`);
    return response.data;
  },

  /**
   * Create a new custom control and attach it to a custom framework
   * @param {Object} controlData - Control data
   * @param {Object} params - Query parameters
   * @param {string} params.framework_id - Custom framework ID to attach this control to (REQUIRED)
   * @param {string} params.module - Module name (optional, will be derived from framework if omitted)
   */
  async createCustomControl(controlData, params = {}) {
    const response = await api.post(`${PREFIX}/custom-controls`, controlData, { params });
    return response.data;
  },

  /**
   * Update a custom control
   * @param {string} controlId
   * @param {Object} updates
   */
  async updateCustomControl(controlId, updates) {
    const response = await api.put(`${PREFIX}/custom-controls/${controlId}`, updates);
    return response.data;
  },

  /**
   * Delete a custom control
   * @param {string} controlId
   */
  async deleteCustomControl(controlId) {
    await api.delete(`${PREFIX}/custom-controls/${controlId}`);
  },

  /**
   * Get all controls for a specific custom framework
   * @param {string} frameworkId
   * @param {Object} params - Query parameters (e.g. { module: 'gemini-enterprise' })
   */
  async getCustomControlsByFramework(frameworkId, params = {}) {
    const response = await api.get(`${PREFIX}/custom-controls/by-framework/${frameworkId}`, { params });
    return response.data;
  },

  // ==========================================================================
  // Controls (Built-in)
  // ==========================================================================

  /**
   * Get all built-in controls
   */
  async getControls(params = {}) {
    const response = await api.get(`${PREFIX}/controls`, { params });
    return response.data;
  },

  /**
   * Get controls for a specific framework
   */
  async getControlsByFramework(frameworkId) {
    const response = await api.get(`${PREFIX}/controls`, {
      params: { framework_id: frameworkId }
    });
    return response.data;
  },

  /**
   * Get a specific control by ID
   */
  async getControlById(controlId) {
    const response = await api.get(`${PREFIX}/controls/${controlId}`);
    return response.data;
  },

  /**
   * Create a new built-in control
   */
  async createControl(controlData) {
    const response = await api.post(`${PREFIX}/controls`, controlData);
    return response.data;
  },

  /**
   * Update a built-in control
   */
  async updateControl(controlId, updates) {
    const response = await api.put(`${PREFIX}/controls/${controlId}`, updates);
    return response.data;
  },

  /**
   * Delete a built-in control
   */
  async deleteControl(controlId) {
    await api.delete(`${PREFIX}/controls/${controlId}`);
  },

  // ==========================================================================
  // Detection Mappings
  // ==========================================================================

  /**
   * Get all detection-to-control mappings
   */
  async getDetectionMappings() {
    const response = await api.get(`${PREFIX}/mappings`);
    return response.data;
  },

  /**
   * Get mappings for a specific detection type
   */
  async getMappingsByDetection(detectionKey) {
    const response = await api.get(`${PREFIX}/mappings/by-detection/${detectionKey}`);
    return response.data;
  },

  /**
   * Get mappings for a specific control
   */
  async getMappingsByControl(controlId) {
    const response = await api.get(`${PREFIX}/mappings/by-control/${controlId}`);
    return response.data;
  },

  /**
   * Create a new detection mapping
   */
  async createDetectionMapping(mappingData) {
    const response = await api.post(`${PREFIX}/mappings`, mappingData);
    return response.data;
  },

  /**
   * Delete a detection mapping
   */
  async deleteDetectionMapping(detectionKey, controlId) {
    await api.delete(`${PREFIX}/mappings/${detectionKey}/${controlId}`);
  },

  /**
   * Get available detection types
   */
  async getDetectionTypes() {
    const response = await api.get(`${PREFIX}/mappings/detection-types`);
    return response.data;
  },

  // ==========================================================================
  // Compliance Reporting (Summary, Controls, Logs)
  // ==========================================================================

  /**
   * Get compliance summary aggregated by framework
   * @param {Object} params - Query parameters
   * @param {number} params.agent_id - Filter by agent ID
   * @param {number} params.framework_id - Filter by framework ID
   * @param {string} params.start_date - ISO format start date
   * @param {string} params.end_date - ISO format end date
   * @param {string} params.time_range - Preset: last_24_hours, last_7_days, last_30_days, last_90_days
   */
  async getComplianceSummary(params = {}) {
    // Normalize: backend uses start_date/end_date; callers may pass start_time/end_time
    const normalized = { ...params };
    if (normalized.start_time && !normalized.start_date) { normalized.start_date = normalized.start_time; }
    if (normalized.end_time && !normalized.end_date) { normalized.end_date = normalized.end_time; }
    delete normalized.start_time;
    delete normalized.end_time;
    const response = await api.get(`${PREFIX}/compliance/summary`, { params: normalized });
    return response.data;
  },

  /**
   * Get compliance summary aggregated by control
   */
  async getControlSummary(params = {}) {
    // Normalize: backend uses start_date/end_date; callers may pass start_time/end_time
    const normalized = { ...params };
    if (normalized.start_time && !normalized.start_date) { normalized.start_date = normalized.start_time; }
    if (normalized.end_time && !normalized.end_date) { normalized.end_date = normalized.end_time; }
    delete normalized.start_time;
    delete normalized.end_time;
    const response = await api.get(`${PREFIX}/compliance/summary/controls`, { params: normalized });
    return response.data;
  },

  /**
   * Get impacted logs for a specific compliance control
   * @param {Object} params - Query parameters
   * @param {number} params.control_id - Required: Control ID
   * @param {number} params.page - Page number
   * @param {number} params.per_page - Items per page
   */
  async getComplianceLogs(params = {}) {
    // Normalize: backend uses start_date/end_date; callers may pass start_time/end_time
    const normalized = { ...params };
    if (normalized.start_time && !normalized.start_date) { normalized.start_date = normalized.start_time; }
    if (normalized.end_time && !normalized.end_date) { normalized.end_date = normalized.end_time; }
    delete normalized.start_time;
    delete normalized.end_time;
    const response = await api.get(`${PREFIX}/compliance/logs`, { params: normalized });
    return response.data;
  },

  /**
   * Get enriched compliance violations for a single span/log.
   * Used by LogDetailsPanel to show which controls are violated for a span.
   *
   * @param {string} logId - The span/log ID (OpenSearch document _id)
   * @returns {Promise<{log_id: string, violations: Array}>}
   *   violations[]: { framework_name, control_number, name,
   *                   remediation_guidance, severity, detection_keys }
   */
  async getViolationsForLog(logId) {
    const response = await api.get(`${PREFIX}/violations/${logId}`);
    return response.data;
  },

  /**
   * Get the raw prompt/response content for a trace span.
   * Used by ComplianceDetailPanel to display the actual interaction that
   * triggered a compliance violation.
   *
   * @param {string} logId - The span document _id (= log_id in impacts index)
   * @param {string} sourceIndex - The source traces index the span lives in
   * @returns {Promise<{log_id, source_index, prompt, response, user, app, timestamp, detections}>}
   */
  async getTraceContent(logId, sourceIndex) {
    const response = await api.get(`${PREFIX}/trace/${logId}`, {
      params: { source_index: sourceIndex },
    });
    return response.data;
  },

  /**
   * Get agents that have compliance impacts for a specific module.
   * @param {Object} params - Query parameters
   * @param {string} params.module - Required: Module name
   * @param {string} params.start_date - Start date (ISO format)
   * @param {string} params.end_date - End date (ISO format)
   * @param {string} params.time_range - Time range type ('custom', etc)
   * @returns {Promise<{agents: Array<{agent_id, agent_name, impact_count}>}>}
   */
  async getAgents(params = {}) {
    const response = await api.get(`${PREFIX}/compliance/agents`, { params });
    return response.data;
  },

  /**
   * Get users that have compliance impacts for a specific module.
   * @param {Object} params - Query parameters
   * @param {string} params.module - Required: Module name
   * @param {string} params.start_date - Start date (ISO format)
   * @param {string} params.end_date - End date (ISO format)
   * @param {string} params.time_range - Time range type ('custom', etc)
   * @returns {Promise<{users: Array<{user_email, impact_count}>}>}
   */
  async getUsers(params = {}) {
    const response = await api.get(`${PREFIX}/compliance/users`, { params });
    return response.data;
  },

  // ==========================================================================
  // Backfill Scan — evaluate historical traces for custom frameworks (ASYNC)
  // ==========================================================================

  /**
   * Trigger an async backfill scan to evaluate historical traces against custom
   * framework controls. Returns immediately with a task_id; poll getBackfillScanStatus()
   * for progress updates.
   *
   * @param {Object} params
   * @param {string} [params.framework_id] - Specific custom framework ID (optional, scans all if omitted)
   * @param {number} params.days - Number of days to scan (1-365, default 30). Ignored when scan_all=true.
   * @param {boolean} [params.scan_all] - When true, scans ALL traces regardless of date.
   * @param {string} [params.module] - Module scope: 'platform', 'gemini-enterprise', 'copilot', 'copilot-studio'
   * @returns {Promise<{task_id, status, message}>}
   */
  async backfillScan({ framework_id, days = 30, scan_all = false, module = '' } = {}) {
    const response = await api.post(`${PREFIX}/evaluation/backfill-scan`, {
      framework_id: framework_id || null,
      days: scan_all ? null : days,
      scan_all,
      module,
    });
    return response.data;
  },

  /**
   * Get the status of an async backfill scan task.
   * Poll this endpoint every few seconds to track progress.
   *
   * @param {string} taskId - The task ID returned from backfillScan()
   * @returns {Promise<{task_id, status, message, days_scanned, total_spans_scanned, spans_with_violations, total_impacts_written, frameworks_evaluated, error}>}
   *   status: 'running' | 'completed' | 'failed'
   */
  async getBackfillScanStatus(taskId) {
    const response = await api.get(`${PREFIX}/evaluation/backfill-scan/${taskId}`);
    return response.data;
  },

  // ==========================================================================
  // Audit Reports — GCS Storage + Metadata
  // ==========================================================================

  /**
   * Save a generated audit report to GCS with friendly name.
   * The backend auto-creates the org-level bucket prefix if needed.
   *
   * @param {Object} params
   * @param {string} params.report_name  - Friendly name (e.g. "Q2 2026 Copilot Audit")
   * @param {Object} params.report       - Full report JSON object
   * @param {string} params.format       - "json" | "csv" (default: "json")
   * @param {string} params.module       - Module name (e.g. "gemini-enterprise")
   * @returns {Promise<{report_id, report_name, file_format, file_size_bytes}>}}
   */
  async saveAuditReport({ report_name, report, format = 'json', module = '' } = {}) {
    // Send the full report body to the compliance microservice.
    // The backend stores the data directly in OpenSearch.
    // nginx only sees this request going to /compliance/api/v1/audit-reports
    // which has no body size limit at the uvicorn/FastAPI level.
    const body = {
      report_name,
      module,
      format,
      report,  // full report JSON — stored in OpenSearch
    };
    const response = await api.post(`${PREFIX}/audit-reports`, body);
    return response.data;
  },

  /**
   * List saved audit reports for the authenticated org.
   * @param {Object} params
   * @param {string} [params.module]           - Filter by module
   * @param {boolean} [params.include_deleted] - Include soft-deleted reports
   * @returns {Promise<{reports: Array, total: number}>}
   */
  async listAuditReports({ module, include_deleted = false } = {}) {
    const response = await api.get(`${PREFIX}/audit-reports`, {
      params: { module: module || undefined, include_deleted },
    });
    return response.data;
  },

  /**
   * Get metadata for a specific audit report.
   * @param {string} reportId
   * @returns {Promise<Object>} Report metadata
   */
  async getAuditReport(reportId) {
    const response = await api.get(`${PREFIX}/audit-reports/${reportId}`);
    return response.data;
  },

  /**
   * Download a stored audit report from OpenSearch in the requested format.
   * Backend generates JSON/CSV on-the-fly and streams it.
   *
   * @param {string} reportId
   * @param {string} [reportName] - Used as the downloaded filename
   * @param {string} [format='json'] - 'json' | 'csv'
   */
  async downloadAuditReport(reportId, reportName = 'audit-report', format = 'json') {
    const response = await api.get(`${PREFIX}/audit-reports/${reportId}/download`, {
      params: { format },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportName}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Soft-delete a saved audit report.
   * @param {string} reportId
   * @returns {Promise<{report_id, deleted: true}>}
   */
  async deleteAuditReport(reportId) {
    const response = await api.delete(`${PREFIX}/audit-reports/${reportId}`);
    return response.data;
  },
};

export default complianceAPI;

